import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/lib/auth/useAuth';
import { useUser } from '@/lib/contexts/UserContext';
import { useOrganization } from '@/lib/contexts/OrganizationContext';
import { cn } from '@/lib/utils';
import { Key, Copy, CheckCircle, Shield, Clock, Code, Rocket } from 'lucide-react';
import TeammateInviteModal from './TeammateInviteModal';
import { trackEvent } from '@/lib/analytics';
import { getApiKeys, createApiKey } from '@/api/api-keys';

/**
 * ApiKeyCTA Component
 * 
 * Displays a prominent call-to-action for creating API keys, especially for
 * new users who haven't integrated with the platform yet. This component
 * forms a critical part of the onboarding flow.
 * 
 * Features:
 * - Detects if user has existing API keys and hides if they do
 * - Shows a modal dialog for creating new API keys
 * - Handles scope selection for fine-grained permissions
 * - Displays the created key only once (can't be retrieved later)
 * - Triggers teammate invite flow after first key creation
 * 
 * @param {string} className - Optional CSS classes to apply to the card
 */

interface ApiKeyCTAProps {
  className?: string;
  onApiKeyCreated?: () => void;
}

interface Scope {
  id: string;
  label: string;
}

const ApiKeyCTA: React.FC<ApiKeyCTAProps> = ({ className, onApiKeyCreated }) => {
  const { isAuthenticated } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { toast } = useToast();
  
  // State management for API key creation flow
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['read:runs', 'write:runs']);
  const [isCreating, setIsCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [showCreatedDialog, setShowCreatedDialog] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [showTeammateInvite, setShowTeammateInvite] = useState(false);
  
  // State for checking existing API keys
  const [hasExistingApiKeys, setHasExistingApiKeys] = useState(false);
  const [isCheckingApiKeys, setIsCheckingApiKeys] = useState(true);

  // Expose the dialog open function globally for the onboarding checklist
  useEffect(() => {
    // Add a global function that the onboarding checklist can call
    (window as Window & { openApiKeyModal?: () => void }).openApiKeyModal = () => {
      setIsDialogOpen(true);
      trackEvent('api_key_modal_opened', { source: 'onboarding_checklist' });
    };

    return () => {
      delete (window as Window & { openApiKeyModal?: () => void }).openApiKeyModal;
    };
  }, []);

  /**
   * Available scopes for API key permissions
   * 
   * These scopes align with the backend's permission model and allow
   * users to create keys with limited access for security best practices.
   * 
   * @see /api/integrations/api-keys endpoint for supported scopes
   */
  const availableScopes: Scope[] = [
    { id: 'read:runs', label: 'Read Runs' },
    { id: 'write:runs', label: 'Write Runs' },
    { id: 'read:alerts', label: 'Read Alerts' },
    { id: 'write:alerts', label: 'Write Alerts' },
    { id: 'read:metrics', label: 'Read Metrics' },
    { id: 'admin', label: 'Admin Access' },
  ];

  /**
   * Check for existing API keys on component mount
   * 
   * This effect fetches the user's existing API keys to determine
   * whether to show or hide the CTA. The CTA should only appear
   * for users who haven't created any API keys yet.
   */
  useEffect(() => {
    const checkExistingApiKeys = async () => {
      if (!isAuthenticated || !user?.id || !organization?.id) {
        setIsCheckingApiKeys(false);
        return;
      }

      try {
        // Fetch actual API keys from the backend using organization ID
        const response = await getApiKeys(organization.id);
        
        if (response.success && response.data) {
          // Hide CTA if user has any existing API keys
          setHasExistingApiKeys(response.data.length > 0);
        } else {
          // On error, default to showing CTA (better UX than hiding it)
          setHasExistingApiKeys(false);
        }
      } catch (error) {
        // On error, default to showing CTA
        console.error('Error checking existing API keys:', error);
        setHasExistingApiKeys(false);
      } finally {
        setIsCheckingApiKeys(false);
      }
    };

    checkExistingApiKeys();
  }, [isAuthenticated, user?.id, organization?.id]);

  /**
   * Handle API key creation
   * 
   * This function orchestrates the API key creation process:
   * 1. Validates input (name and scopes)
   * 2. Calls the backend API to create the key
   * 3. Shows the created key to the user
   * 4. Triggers the teammate invite flow for first-time users
   * 
   * The created key is shown only once and cannot be retrieved later
   * for security reasons.
   */
  const handleCreateApiKey = async () => {
    // Validate input
    if (!keyName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a name for your API key',
        variant: 'destructive',
      });
      return;
    }

    if (selectedScopes.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one scope',
        variant: 'destructive',
      });
      return;
    }

    if (!user?.id || !organization?.id) {
      toast({
        title: 'Error',
        description: 'Authentication error. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    trackEvent('api_key_creation_started', {
      scopes_count: selectedScopes.length,
      has_admin_scope: selectedScopes.includes('admin')
    });

    try {
      // Create API key through the backend using organization ID
      const response = await createApiKey({
        name: keyName.trim(),
        scopes: selectedScopes,
        userId: user.id,
        organizationId: organization.id,
      });

      if (response.success && response.data) {
        // Store the created key - this is the only time it's visible
        setCreatedKey(response.data.key);
        setIsDialogOpen(false);
        setShowCreatedDialog(true);
        
        // Track successful creation
        trackEvent('api_key_created', {
          scopes_count: selectedScopes.length,
          has_admin_scope: selectedScopes.includes('admin'),
          is_first_key: !hasExistingApiKeys
        });
        
        // Show teammate invite modal for first API key
        if (!hasExistingApiKeys) {
          setShowTeammateInvite(true);
        }
        
        // Update state to hide CTA
        setHasExistingApiKeys(true);
        onApiKeyCreated?.();
      } else {
        throw new Error(response.error || 'Failed to create API key');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create API key. Please try again.',
        variant: 'destructive',
      });
      
      trackEvent('api_key_creation_failed', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Copy API key to clipboard
   * 
   * Uses the modern Clipboard API with fallback for older browsers.
   * Shows visual feedback when the key is successfully copied.
   */
  const copyToClipboard = () => {
    if (!createdKey) return;

    navigator.clipboard.writeText(createdKey).then(() => {
      setCopiedToClipboard(true);
      toast({
        title: 'Copied!',
        description: 'API key copied to clipboard',
      });
      
      trackEvent('api_key_copied', {
        method: 'clipboard_api'
      });
      
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopiedToClipboard(false), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = createdKey;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setCopiedToClipboard(true);
      toast({
        title: 'Copied!',
        description: 'API key copied to clipboard',
      });
      
      trackEvent('api_key_copied', {
        method: 'fallback'
      });
      
      setTimeout(() => setCopiedToClipboard(false), 2000);
    });
  };

  /**
   * Reset dialog state when closing
   * 
   * Ensures the form is clean for the next time the user opens it.
   */
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setKeyName('');
    setSelectedScopes(['read:runs', 'write:runs']);
  };

  // Don't render anything while checking for existing keys
  if (isCheckingApiKeys) {
    return null;
  }

  // Don't render if user already has API keys
  if (hasExistingApiKeys) {
    return null;
  }

  // Don't render if user is not authenticated
  if (!isAuthenticated || !user || !organization) {
    return null;
  }

  return (
    <>
      {/* Main CTA Card */}
      <Card className={className} data-component="api-key-cta">
        <CardHeader>
          <div className="flex items-center gap-2">
            {/* Key icon with branded styling */}
            <div className="p-2 bg-blue-100 rounded-lg">
              <Key className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Get Started with API Keys</CardTitle>
              <CardDescription>
                Integrate EvalOps into your CI/CD pipeline with programmatic access
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Value proposition - single focused benefit */}
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-600">
                Secure, scoped access to your evaluation data
              </span>
            </div>
            
            {/* Primary CTA */}
            <Button 
              onClick={() => {
                setIsDialogOpen(true);
                trackEvent('api_key_cta_clicked', {
                  location: 'dashboard'
                });
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Key className="mr-2 h-4 w-4" />
              Create Your First API Key
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* API Key Creation Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Generate a new API key to authenticate your applications with EvalOps.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Key Name Input */}
            <div className="grid gap-2">
              <Label htmlFor="key-name">Key Name</Label>
              <Input
                id="key-name"
                placeholder="e.g., Production CI/CD"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                disabled={isCreating}
              />
              <p className="text-sm text-gray-500">
                A descriptive name to help you identify this key later
              </p>
            </div>
            
            {/* Scope Selection */}
            <div className="grid gap-2">
              <Label>Permissions</Label>
              <div className="space-y-2">
                {availableScopes.map((scope) => (
                  <div key={scope.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={scope.id}
                      checked={selectedScopes.includes(scope.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedScopes([...selectedScopes, scope.id]);
                        } else {
                          setSelectedScopes(selectedScopes.filter(s => s !== scope.id));
                        }
                      }}
                      disabled={isCreating}
                    />
                    <Label
                      htmlFor={scope.id}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {scope.label}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500">
                Select the permissions this key should have. Follow the principle of least privilege.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDialogClose}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateApiKey}
              disabled={isCreating || !keyName.trim() || selectedScopes.length === 0}
            >
              {isCreating ? 'Creating...' : 'Create API Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Created Key Dialog */}
      <Dialog open={showCreatedDialog} onOpenChange={setShowCreatedDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <DialogTitle>API Key Created Successfully</DialogTitle>
            </div>
            <DialogDescription>
              Make sure to copy your API key now. You won't be able to see it again!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* API Key Display */}
            <div className="relative">
              <div className="p-4 bg-gray-50 rounded-lg font-mono text-sm break-all">
                {createdKey}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className={cn(
                  "absolute top-2 right-2",
                  copiedToClipboard && "text-green-600"
                )}
                onClick={copyToClipboard}
              >
                {copiedToClipboard ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            
            {/* Usage Instructions */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Quick Start:</h4>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Code className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium">CLI Usage:</p>
                    <code className="block mt-1 p-2 bg-gray-100 rounded text-xs">
                      export EVALOPS_API_KEY="{createdKey}"
                    </code>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Rocket className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium">CI/CD Integration:</p>
                    <p className="mt-1">Add this key as a secret in your pipeline configuration</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => {
                setShowCreatedDialog(false);
                setCreatedKey(null);
              }}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teammate Invite Modal */}
      {showTeammateInvite && (
        <TeammateInviteModal
          open={showTeammateInvite}
          onClose={() => setShowTeammateInvite(false)}
        />
      )}
    </>
  );
};

export default ApiKeyCTA;